import { type FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Draggable, DragDropContext } from 'react-beautiful-dnd';
import DelayedDroppableWrapper from '@editor2/components/DelayedDroppableWrapper';
import { BsFileEarmarkPlus, BsTrash } from 'react-icons/bs';

import { movePage, deletePageFromChapter } from '@editor2/reducers/editor/editorSlice';
import { type RootState } from '@editor2/reducers/root';
import {
	headerContainer,
	contentBlock,
	pageContainer
} from '@editor2/containers/ChapterPage/styles';
import AddPageModal from '@editor2/components/AddPageModal';
import { getPageNumberIfExistsAndNotSpace } from '@editor2/services/pagesService';

interface Props {
	chapterId: string;
	courseId: number;
}

const PagesList: FC<Props> = (props) => {
	const { chapterId, courseId } = props;

	const dispatch = useDispatch();
	const { chapters, pages, course } = useSelector((state: RootState) => state.courses[courseId]);
	const chapterPages = chapters.entities[chapterId].pages;

	const [isAddPageModalShown, setAddPageModalShown] = useState(false);

	const onDeletePageClick = (pageId: string) => {
		const pageName = pages.entities[pageId].name;
		// eslint-disable-next-line no-alert
		if (window.confirm(`Are you sure to delete "${pageName}" page group?`)) {
			dispatch(deletePageFromChapter({ chapterId, pageId, courseId }));
		}
	};

	const onDragEnd = (result) => {
		const { source, destination, draggableId, type } = result;
		const { droppableId: sourceDroppableId } = source;
		const { droppableId: destinationDroppableId, index: destinationIndex } = destination;

		if (type === 'Page') {
			dispatch(
				movePage({
					sourceDroppableId,
					destinationDroppableId,
					destinationIndex,
					pageId: draggableId,
					courseId
				})
			);
		}
	};

	const Page = ({ pageId, index }) => {
		return (
			<Draggable draggableId={pageId} index={index}>
				{(provided) => (
					<div
						className={pageContainer}
						ref={provided.innerRef}
						{...provided.draggableProps}
						{...provided.dragHandleProps}>
						<div className={headerContainer}>
							<div className="menu">
								<BsFileEarmarkPlus onClick={() => setAddPageModalShown(true)} />
								<BsTrash onClick={() => onDeletePageClick(pageId)} />
							</div>

							<div className="headerText">
								<div>
									<span>Page{getPageNumberIfExistsAndNotSpace(pages.entities[pageId])}: </span>
									<Link to={`/courses/${course.id}/chapters/${chapterId}/pages/${pageId}`}>
										{pages.entities[pageId].name}
									</Link>
								</div>
							</div>
						</div>

						{isAddPageModalShown && (
							<AddPageModal
								chapterId={chapterId}
								index={index + 1}
								onHide={() => setAddPageModalShown(false)}
								isShown={isAddPageModalShown}
								courseId={courseId}
							/>
						)}
					</div>
				)}
			</Draggable>
		);
	};

	return (
		<div className={contentBlock}>
			<DragDropContext onDragEnd={onDragEnd}>
				<DelayedDroppableWrapper droppableId={chapterId} type="Page">
					{(provided) => (
						<div ref={provided.innerRef} {...provided.droppableProps}>
							{chapterPages.map((pageId, index) => (
								<Page pageId={pageId} index={index} key={pageId} />
							))}
							{provided.placeholder}
						</div>
					)}
				</DelayedDroppableWrapper>
			</DragDropContext>
		</div>
	);
};

export default PagesList;
